import type {
  AppWeatherApi,
  GeoLocation,
  WeatherForecast,
  HourlyWeatherPoint,
  DayWeatherSummary,
  WeatherProvider,
  WeatherPreferences,
} from '@yada/types';
import { MetNorwayProvider, sanitizeCoordinates } from './metNorwayProvider';
import { toLocalDate } from '@yada/types';

const CACHE_PREFIX = 'yada:weather:cache:v3:';
const PREFERENCES_KEY = 'yada:weather:preferences';

/**
 * Geographic cache key generator.
 * Rounds coordinates to 2 decimal places (~1.1km grid) to coalesce nearby queries,
 * adhering to geographic caching requirements.
 */
export function getGeoCacheKey(latitude: number, longitude: number): string {
  const roundedLat = Math.round(latitude * 100) / 100;
  const roundedLon = Math.round(longitude * 100) / 100;
  return `${CACHE_PREFIX}${roundedLat}_${roundedLon}`;
}

export interface WeatherServiceOptions {
  provider?: WeatherProvider;
  defaultLocation?: GeoLocation;
}

function formatLocalDate(d: Date): string {
  return toLocalDate(d);
}

export class WeatherService implements AppWeatherApi {
  private provider: WeatherProvider;
  private fallbackLocation: GeoLocation;
  private inFlightRequests = new Map<string, Promise<WeatherForecast>>();

  constructor(options: WeatherServiceOptions = {}) {
    this.provider = options.provider || new MetNorwayProvider();
    this.fallbackLocation = options.defaultLocation || {
      latitude: 59.9139,
      longitude: 10.7522,
      name: 'Oslo',
      countryCode: 'NO',
    };
  }

  async isEnabled(): Promise<boolean> {
    const prefs = await this.getPreferences();
    return prefs.enabled;
  }

  async getPreferences(): Promise<WeatherPreferences> {
    try {
      const raw = localStorage.getItem(PREFERENCES_KEY);
      if (raw) {
        return JSON.parse(raw) as WeatherPreferences;
      }
    } catch {}
    // Default: not enabled until explicit user opt-in/onboarding
    return {
      enabled: false,
      locationMode: 'custom',
      customLocation: undefined,
      browserLocation: undefined,
    };
  }

  async setPreferences(prefsUpdate: Partial<WeatherPreferences>): Promise<void> {
    const current = await this.getPreferences();
    const updated: WeatherPreferences = {
      ...current,
      ...prefsUpdate,
    };
    try {
      localStorage.setItem(PREFERENCES_KEY, JSON.stringify(updated));
    } catch {}
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('yada:weather:changed', { detail: updated }));
    }
  }

  async requestBrowserLocation(): Promise<GeoLocation | null> {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      return null;
    }
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const sanitized = sanitizeCoordinates(pos.coords.latitude, pos.coords.longitude);
          const loc: GeoLocation = {
            latitude: sanitized.lat,
            longitude: sanitized.lon,
            altitudeMeters: pos.coords.altitude !== null ? Math.round(pos.coords.altitude) : undefined,
          };
          // Save to browserLocation in preferences
          await this.setPreferences({ browserLocation: loc });
          resolve(loc);
        },
        (err) => {
          console.warn('[WeatherService] Geolocation permission denied or unavailable:', err);
          resolve(null);
        },
        { timeout: 10000, maximumAge: 600000, enableHighAccuracy: false }
      );
    });
  }

  async getDefaultLocation(): Promise<GeoLocation | null> {
    const prefs = await this.getPreferences();
    if (!prefs.enabled) return null;

    if (prefs.locationMode === 'custom' && prefs.customLocation) {
      return prefs.customLocation;
    }
    if (prefs.locationMode === 'browser' && prefs.browserLocation) {
      return prefs.browserLocation;
    }
    // If enabled, fallback gracefully to customLocation, browserLocation, or fallbackLocation
    return prefs.customLocation || prefs.browserLocation || this.fallbackLocation;
  }

  async setDefaultLocation(location: GeoLocation): Promise<void> {
    await this.setPreferences({
      locationMode: 'custom',
      customLocation: location,
    });
  }

  async getForecast(location?: GeoLocation): Promise<WeatherForecast | null> {
    const prefs = await this.getPreferences();
    // Opt-in protection: Do not query upstream if weather is not enabled
    if (!prefs.enabled && !location) {
      return null;
    }

    const targetLoc = location || (await this.getDefaultLocation());
    if (!targetLoc) return null;

    const cacheKey = getGeoCacheKey(targetLoc.latitude, targetLoc.longitude);

    // 1. Check local cache
    try {
      const cachedRaw = localStorage.getItem(cacheKey);
      if (cachedRaw) {
        const cachedForecast: WeatherForecast = JSON.parse(cachedRaw);
        const expiresAt = new Date(cachedForecast.expiresAt).getTime();
        if (Date.now() < expiresAt) {
          return cachedForecast;
        }
      }
    } catch {}

    // 2. Check if a request is already in-flight for this coordinate cell (deduplication)
    if (this.inFlightRequests.has(cacheKey)) {
      return this.inFlightRequests.get(cacheKey)!;
    }

    // 3. Fetch from provider with graceful fallback
    const fetchPromise = (async () => {
      try {
        const fresh = await this.provider.fetchForecast(targetLoc);
        try {
          localStorage.setItem(cacheKey, JSON.stringify(fresh));
        } catch {}
        return fresh;
      } catch (err) {
        console.warn('[WeatherService] Upstream fetch failed, attempting stale cache fallback:', err);
        // Fall back to stale cache if available
        try {
          const staleRaw = localStorage.getItem(cacheKey);
          if (staleRaw) {
            return JSON.parse(staleRaw) as WeatherForecast;
          }
        } catch {}
        return null;
      } finally {
        this.inFlightRequests.delete(cacheKey);
      }
    })();

    this.inFlightRequests.set(cacheKey, fetchPromise as Promise<WeatherForecast>);
    return fetchPromise;
  }

  async at(location?: GeoLocation, timestamp?: string | Date): Promise<HourlyWeatherPoint | null> {
    const forecast = await this.getForecast(location);
    if (!forecast || forecast.hourly.length === 0) return null;

    if (!timestamp) {
      return forecast.current;
    }

    const targetTimeMs = typeof timestamp === 'string' ? new Date(timestamp).getTime() : timestamp.getTime();
    if (isNaN(targetTimeMs)) return forecast.current;

    // Find the hourly point closest to the requested timestamp
    let closest = forecast.hourly[0];
    let minDiff = Math.abs(new Date(closest.time).getTime() - targetTimeMs);

    for (let i = 1; i < forecast.hourly.length; i++) {
      const pt = forecast.hourly[i];
      const diff = Math.abs(new Date(pt.time).getTime() - targetTimeMs);
      if (diff < minDiff) {
        minDiff = diff;
        closest = pt;
      }
    }

    return closest;
  }

  async range(
    location: GeoLocation | undefined,
    start: string | Date,
    end: string | Date
  ): Promise<HourlyWeatherPoint[]> {
    const forecast = await this.getForecast(location);
    if (!forecast || forecast.hourly.length === 0) return [];

    const startMs = typeof start === 'string' ? new Date(start).getTime() : start.getTime();
    const endMs = typeof end === 'string' ? new Date(end).getTime() : end.getTime();

    return forecast.hourly.filter(pt => {
      const ptMs = new Date(pt.time).getTime();
      return ptMs >= startMs && ptMs <= endMs;
    });
  }

  async day(location?: GeoLocation, date?: string | Date): Promise<DayWeatherSummary | null> {
    const forecast = await this.getForecast(location);
    if (!forecast || forecast.daily.length === 0) return null;

    let targetDateStr: string;
    if (!date) {
      targetDateStr = formatLocalDate(new Date());
    } else if (typeof date === 'string') {
      targetDateStr = date.split('T')[0];
    } else {
      targetDateStr = formatLocalDate(date);
    }

    const match = forecast.daily.find(d => d.date === targetDateStr);
    return match || forecast.daily[0] || null;
  }
}

// Singleton shared instance
let defaultWeatherService: WeatherService | null = null;

export function getWeatherService(): WeatherService {
  if (!defaultWeatherService) {
    defaultWeatherService = new WeatherService();
  }
  return defaultWeatherService;
}
